import {
  FieldNodeStateWithId,
  FormValidationResult,
  MasonryEngineStateController,
  MasonryEngineValidationController,
  MasonryEngineMessagingController,
} from '@leagueplatform/masonry-engine';

/**
 * Reusable utilities for form validation extracted from form-submission-base.ts
 * These utilities can be used by any action handler that needs to validate form fields.
 */

/**
 * Utility function to gather all visible form field nodes for a given form
 */
export const getVisibleFormFieldNodes = (
  formId: string,
  stateController: MasonryEngineStateController,
): FieldNodeStateWithId[] => {
  const fieldNodeIds = stateController.getFormFieldNodesMap(formId);

  if (!fieldNodeIds) {
    throw new Error(`No form with the form ID ${formId} was found!`);
  }

  // Get all visible field nodes
  return fieldNodeIds
    .map((id: string) => {
      const nodeState = stateController.getNodeState(id);
      if (!nodeState) {
        throw new Error(`Node state for nodeId ${id} not found!`);
      }
      return {
        nodeId: id,
        ...nodeState,
      } as FieldNodeStateWithId;
    })
    .filter((node: FieldNodeStateWithId) => node?.isHidden !== true);
};

export const updateFieldNodesFromValidationResult = (
  validationResult: FormValidationResult,
  fieldNodes: FieldNodeStateWithId[],
  validationController: typeof MasonryEngineValidationController,
  stateController: MasonryEngineStateController,
  messagingController: MasonryEngineMessagingController,
  hasFormLevelMessage?: boolean,
) => {
  // Update fields with validation status every time.
  // In case where there are nodes with invalid result, it should update state
  // of those nodes with validation status and message.
  // In case when the fields become valid again, it should remove the validation
  // previously applied validation status and message.

  validationController.updateFieldNodesWithValidationMessages(
    validationResult,
    fieldNodes,
    stateController,
  );

  // If there are validation errors, and if there is no form level status message,
  // focus the first invalid field and return false
  if (validationResult.length > 0 && !hasFormLevelMessage) {
    validationController.focusFirstInvalidField(
      validationResult,
      messagingController,
    );
  }
};

/**
 * Utility function to validate form fields and update their status messages
 * Returns true if validation passes (no errors), false if there are validation errors
 */
export const validateAndUpdateFieldNodes = (
  fieldNodes: FieldNodeStateWithId[],
  validationController: typeof MasonryEngineValidationController,
  stateController: MasonryEngineStateController,
  messagingController: MasonryEngineMessagingController,
  hasFormLevelMessage?: boolean,
) => {
  // Validate form fields and get validation status of failed nodes
  const validationResult = validationController.validateFormFields(fieldNodes);

  updateFieldNodesFromValidationResult(
    validationResult,
    fieldNodes,
    validationController,
    stateController,
    messagingController,
    hasFormLevelMessage,
  );

  // If there are validation errors, return `false` to indicate failure
  return !validationResult.length;
};
